import { useRef, useCallback, useEffect, useState } from 'react';

/**
 * Horizontal swipe-to-dismiss for a left-side drawer with two navigation levels.
 *
 * - Level 1 (main menu): swipe left → close drawer
 * - Level 2 (submenu):   swipe left → go back to Level 1
 *
 * Returns inline styles for both the drawer panel and the overlay,
 * plus a ref to attach to the drawer `<aside>`.
 */
const useDrawerSwipe = ({ onClose, onBack, isSubmenu, isOpen }) => {
  const drawerRef = useRef(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSnapping, setIsSnapping] = useState(false);

  const closeTimerRef = useRef(null);
  const snapTimerRef = useRef(null);

  const touch = useRef({
    startX: null,
    startY: null,
    dir: null, // 'h' | 'v' | null
    offset: 0,
  });

  const THRESHOLD = 80;   // px to trigger action
  const DIR_LOCK = 10;    // px before locking direction

  /* ── Reset helper ── */
  const reset = useCallback(() => {
    touch.current = { startX: null, startY: null, dir: null, offset: 0 };
    setSwipeOffset(0);
    setIsSnapping(false);
  }, []);

  /* ── Cleanup timers on unmount ── */
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      if (snapTimerRef.current) clearTimeout(snapTimerRef.current);
    };
  }, []);

  /* ── Reset when drawer opens / closes ── */
  useEffect(() => {
    reset();
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (snapTimerRef.current) {
      clearTimeout(snapTimerRef.current);
      snapTimerRef.current = null;
    }
  }, [isOpen, reset]);

  /* ── Touch handlers ── */
  const handleStart = useCallback((e) => {
    if (isSnapping) return;
    touch.current.startX = e.touches[0].clientX;
    touch.current.startY = e.touches[0].clientY;
    touch.current.dir = null;
    touch.current.offset = 0;
  }, [isSnapping]);

  const handleMove = useCallback((e) => {
    const t = touch.current;
    if (t.startX === null || isSnapping) return;

    const dx = e.touches[0].clientX - t.startX;
    const dy = e.touches[0].clientY - t.startY;

    // Lock direction after small movement
    if (!t.dir) {
      if (Math.abs(dx) >= DIR_LOCK || Math.abs(dy) >= DIR_LOCK) {
        t.dir = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
      }
      if (t.dir !== 'h') return;
    }
    if (t.dir !== 'h') return;

    // Only respond to leftward swipe (negative dx)
    if (dx < 0) {
      t.offset = dx;
      setSwipeOffset(dx);
      e.preventDefault();
    } else if (t.offset < 0) {
      // Finger reversed — clamp at 0
      t.offset = 0;
      setSwipeOffset(0);
    }
  }, [isSnapping]);

  const handleEnd = useCallback(() => {
    const t = touch.current;
    const el = drawerRef.current;

    if (t.dir === 'h' && t.offset < -THRESHOLD) {
      if (isSubmenu) {
        /* Level 2 → Level 1: snap drawer back + navigate */
        setSwipeOffset(0);
        setIsSnapping(true);
        touch.current = { startX: null, startY: null, dir: null, offset: 0 };
        onBack?.();
        snapTimerRef.current = setTimeout(() => setIsSnapping(false), 300);
      } else {
        /* Level 1 → close: animate fully off-screen, then close */
        const width = el?.offsetWidth || 360;
        setSwipeOffset(-width);
        setIsSnapping(true);
        touch.current = { startX: null, startY: null, dir: null, offset: 0 };
        closeTimerRef.current = setTimeout(() => {
          onClose?.(true);
          // reset happens via useEffect[isOpen] when isOpen goes false
        }, 300);
      }
    } else if (t.offset < 0) {
      /* Below threshold — spring back */
      setSwipeOffset(0);
      setIsSnapping(true);
      touch.current = { startX: null, startY: null, dir: null, offset: 0 };
      snapTimerRef.current = setTimeout(() => setIsSnapping(false), 300);
    } else {
      reset();
    }
  }, [isSubmenu, onBack, onClose, reset]);

  /* ── Attach / detach listeners ── */
  useEffect(() => {
    const el = drawerRef.current;
    if (!el || !isOpen) return;

    el.addEventListener('touchstart', handleStart, { passive: true });
    el.addEventListener('touchmove', handleMove, { passive: false });
    el.addEventListener('touchend', handleEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', handleStart);
      el.removeEventListener('touchmove', handleMove);
      el.removeEventListener('touchend', handleEnd);
    };
  }, [isOpen, handleStart, handleMove, handleEnd]);

  /* ── Compute inline styles ── */
  const easeCurve = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)';
  const snapTransition = `transform 0.3s ${easeCurve}`;
  const opacityTransition = `opacity 0.3s ${easeCurve}`;

  const drawerSwipeStyle = (() => {
    if (swipeOffset < 0) {
      return {
        transform: `translateX(${swipeOffset}px)`,
        transition: isSnapping ? snapTransition : 'none',
        willChange: isSnapping ? undefined : 'transform',
      };
    }
    if (isSnapping) {
      return {
        transform: 'translateX(0)',
        transition: snapTransition,
      };
    }
    return {};
  })();

  const drawerWidth = drawerRef.current?.offsetWidth || 360;

  const overlaySwipeStyle = (() => {
    if (swipeOffset < 0) {
      return {
        opacity: Math.max(0, 1 + swipeOffset / drawerWidth),
        transition: isSnapping ? opacityTransition : 'none',
      };
    }
    if (isSnapping) {
      return {
        opacity: 1,
        transition: opacityTransition,
      };
    }
    return {};
  })();

  const isSwiping = swipeOffset !== 0 || isSnapping;

  return {
    drawerRef,
    drawerSwipeStyle,
    overlaySwipeStyle,
    isSwiping,
  };
};

export default useDrawerSwipe;
